import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import { Chart as HighchartsReact } from '@highcharts/react'
import Spinner from '../../../util/spinner-shim'
import moment from 'moment'
import { useLocation } from 'react-router-dom'

import {
  displayFormula,
  litersToUserPreference,
} from 'altrac-conversions'

import styles from '../../../../css/charts.module.css'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import AppStore from '../../../stores/AppStore'

import ChartDataLoading from './ChartDataLoading'

function makeColumns (label, labelTwo, statType) {
  if (statType === 'gpm') {
    return ([
      {
        type: 'date',
        label: 'Time',
      },
      {
        type: 'string',
        role: 'tooltip',
        p: { html: true },
      },
      {
        type: 'number',
        labelTwo,
      },
    ])
  }
  return ([
    {
      type: 'date',
      label: 'Time',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label,
    },
  ])
}

function makeRows (flowStats, series, label, labelTwo, statType, formula, physical, context, multiplier, precision, unitsOfFlow, unitsOfVolume) {
  const rows = []
  let tempData = []
  let tempDataSorted = []
  if (statType === 'gpm') {
    tempData = series
    tempDataSorted = tempData.slice().sort((a, b) => new Date(b.date) - new Date(a.date))
  } else {
    tempData = flowStats
    tempDataSorted = tempData.slice().sort((a, b) => new Date(b.date_begin) - new Date(a.date_begin))
  }
  if (tempData) {
    tempDataSorted.forEach((item, index, arr) => {
      const row = []
      const currentSensorData = item
      let lastSensorData = item
      if (index === arr.length - 1) {
        lastSensorData = item
      }
      if (statType === 'gpm') {
        const { ratePerMin } = item

        const dateKeeper = new Date(item.date)
        dateKeeper.setHours(dateKeeper.getHours() + 12)
        row[0] = moment(item.date).format('lll')
        row[1] = `
          <div style='padding:0px 10px;min-width:160px;'>
            <h6>${moment(item.date).format('lll')}</h6>
            <h6>${label}: ${(ratePerMin || ratePerMin === 0) ? ratePerMin : 'Error'}</h6>
            <h6>${labelTwo}: ${(ratePerMin || ratePerMin === 0) ? ratePerMin : 'Error'}</h6>
          </div>
        `
        row[2] = litersToUserPreference(ratePerMin, unitsOfFlow, 'flow', 0)
        // row[3] = ratePerMin;
        rows.push(row)
      } else if (statType === 'cumulative') {
        let value = item.flow_cumulative

        value = litersToUserPreference(item.flow_cumulative, unitsOfVolume, 'volume', 2)

        const dateKeeper = new Date(item.date_begin)
        dateKeeper.setHours(dateKeeper.getHours() + 12)
        row[0] = moment(item.date_begin).format('lll')
        row[1] = `
          <div style='padding:0px 10px;min-width:160px;'>
            <h6>${moment(item.date_begin).format('lll')}</h6>
            <h6>${label}: ${(value || value === 0) ? value : 'Error'}</h6>
          </div>
        `
        row[2] = value
        rows.push(row)
      } else if (item.stats[statType] || item.stats[statType] === 0) {
        let value = item.stats[statType]
        if (tempData) {
          lastSensorData = tempData
        }
        if (formula) {
          value = displayFormula(
            formula,
            multiplier,
            precision,
            context,
            statType,
            currentSensorData || {},
            lastSensorData || {},
            physical || {}
          )
          if (value === 'OFF' || value === 'Off' || value === 'STP' || value === 'ERL' || value === 'ERH') {
            value = 0
          } else if (value === 'ON' || value === 'RUN' || value === 'Low') {
            value = 1
          } else if (value === 'High') {
            value = 2
          }
        }
        // since we only do whole days in stats mode, we shift date by 12 hours in order to show
        // in the right place
        const dateKeeper = new Date(item.date_begin)
        dateKeeper.setHours(dateKeeper.getHours() + 12)
        row[0] = dateKeeper
        row[1] = `
          <div style='padding:0px 10px;min-width:160px;'>
            <h6>${dateKeeper.toLocaleDateString()}</h6>
            <h6>${label}: ${(value || value === 0) ? value : 'Error'}</h6>
          </div>
        `
        row[2] = value
        rows.push(row)
      }
    })
  }
  return rows
}

const TableCell = function TableCell ({ value }) {
  const returnVal = (
    <td>
      {value}
    </td>
  )
  return returnVal
}

TableCell.propTypes = {
  value: PropTypes.node,
}

const TableRow = function TableRow ({ data }) {
  const cells = []
  for (let i = 0; i < data.length; i += 1) {
    if (i !== 1) {
      let cellValue
      if (data[i] !== null) {
        cellValue = data[i]
      } else if (data[i] !== null) {
        cellValue = data[i]
      } else {
        cellValue = 'N/A'
      }

      cells.push(
        <TableCell key={i} value={cellValue} />
      )
    }
  }
  return (
    <tr>
      {cells}
    </tr>
  )
}

TableRow.propTypes = {
  data: PropTypes.array,
}

const Table = function Table ({
  flowStats,
  series,
  label,
  labelTwo,
  physical,
  statType,
  formula,
  multiplier,
  precision,
  unitsOfFlow,
  unitsOfVolume,
  volumeTitle,
}) {
  const { tempConv } = AppStore.getUser()?.locale || { tempConv: 'c' }

  // if (flowStats.daily_summary.length < 1) {
  //   return (
  //     <div className="loading">
  //       <Spinner name="double-bounce" />
  //     </div>
  //   );
  // }

  const rows = makeRows(
    flowStats,
    series,
    label,
    labelTwo,
    statType,
    formula,
    physical,
    { tempConv },
    multiplier,
    precision,
    unitsOfFlow,
    unitsOfVolume,
    volumeTitle
  )
  const rowsArr = []
  for (let i = 0; i < rows.length; i++) {
    rowsArr.push(
      <TableRow key={i} data={rows[i]} />
    )
  }
  const columns = makeColumns(label, labelTwo, statType)
  const columnArr = []
  for (let i = 0; i < columns.length; i += 1) {
    columnArr.push(columns[i].label)
  }
  return (
    <table className='table table-striped'>
      <thead>
        <TableRow data={columnArr} />
      </thead>
      <tbody>
        {rowsArr}
      </tbody>
    </table>
  )
}

Table.propTypes = {
  flowStats: PropTypes.arrayOf(PropTypes.object),
  physical: PropTypes.object,
  statType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  multiplier: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  precision: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  formula: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  unitsOfFlow: PropTypes.string,
  unitsOfVolume: PropTypes.string,
  volumeTitle: PropTypes.string,
}

export const ChartFlow = function ChartFlow ({
  flowStats,
  flowReady,
  statType,
  unitsOfFlow,
  unitsOfVolume,
  volumeTitle,
}) {
  if (
    flowReady !== 'loaded' ||
    typeof flowStats === 'undefined'
  ) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }

  const dailySummary = flowStats.daily_summary
  const sData = flowStats.series
  const dailyFlow = []
  const dailyTotal = []
  const cumulative = []

  let series = []
  let newYAxis = {}
  let cumulativeTotal = 0
  let tempCTotal = 0
  let dataColor = ['#284047', '#6AAEF8']
  if (statType === 'gpm') {
    const sortedSData = sData.slice().sort((a, b) => new Date(a.date) - new Date(b.date))
    sortedSData.forEach((item) => {
      dailyFlow.push([moment(item.date).valueOf(), litersToUserPreference(item.ratePerMin, unitsOfFlow, 'flow', 2)])
    })
    dailySummary.forEach((item) => {
      tempCTotal += item.flow_cumulative
      cumulative.push([moment(item.date_begin).valueOf(), litersToUserPreference(tempCTotal, unitsOfVolume, 'volume', 2)])
    })
    cumulativeTotal = litersToUserPreference(tempCTotal, unitsOfVolume, 'volume', 2)
    series = [
      {
        name: `Flow Rate (${unitsOfFlow || 'gpm'})`,
        animation: false,
        data: dailyFlow,
        type: 'spline',
        yAxis: 1,
      },
      {
        name: `${volumeTitle}`,
        animation: false,
        data: cumulative,
        type: 'spline',
        yAxis: 0,
      },
    ]
    newYAxis = [
      {
      // gridLineWidth: 0,
        labels: {
          format: '{value}',
          style: {
            color: '#6AAEF8',
          },
        },
        title: {
          text: `${volumeTitle}`,
          style: {
            color: '#6AAEF8',
          },
        },
        opposite: true,
      // plotLines,
      },
      {
        gridLineWidth: 0,
        labels: {
          format: '{value}',
          style: {
            color: '#284047',
          },
        },
        title: {
          text: `Flow Rate (${unitsOfFlow || 'gpm'})`,
          style: {
            color: '#284047',
          },
        },
      }]
  } else if (statType === 'cumulative') {
    dailySummary.forEach((item) => {
      dailyTotal.push([moment(item.date_begin).valueOf(), litersToUserPreference(item.flow_cumulative, unitsOfVolume, 'volume', 2)])
      tempCTotal += item.flow_cumulative
    })
    cumulativeTotal = litersToUserPreference(tempCTotal, unitsOfVolume, 'volume', 2)
    series = [
      {
        name: `Cumulative (${volumeTitle})`,
        animation: false,
        data: dailyTotal,
        type: 'column',
      },
    ]
    newYAxis = [
      {
      // gridLineWidth: 0,
        labels: {
          format: '{value}',
          style: {
            color: '#6AAEF8',
          },
        },
        title: {
          text: `${volumeTitle}`,
          style: {
            color: '#6AAEF8',
          },
        },
        opposite: true,
      // plotLines,
      }]
    dataColor = ['#6AAEF8']
  }

   const options = {
    accessibility: {
      enabled: false,
    },
    time: {
      useUTC: false,
    },
    title: {
      text: '',
    },
    tooltip: {
      xDateFormat: '%b %e %H:%M',
      shared: true,
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%e %b',
        month: '%b %e',
        year: '%b',
      },
      title: {
        enabled: false,
      },
      tickInterval: 1000 * 60 * 60 * 24,
      minRange: 3600000,
      // tickPixelInterval: 75,
    },
    yAxis: newYAxis,
    series,
    colors: dataColor,
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
  }

  return (
    <div className={styles.chart}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
      />

      {statType === 'cumulative'
        ? (
          <div className={styles.cumulativeTotalWrapper}>
            <h4>{cumulativeTotal}</h4>
            <p>
              Cumulative Total (
              {volumeTitle}
              )
            </p>
          </div>
          )
        : null}
    </div>
  )
}

ChartFlow.propTypes = {
  flowStats: PropTypes.arrayOf(PropTypes.object),
  statType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  unitsOfFlow: PropTypes.string,
  unitsOfVolume: PropTypes.string,
  volumeTitle: PropTypes.string,
}

function TelemetryFlow (props) {
  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const {
    device,
    periodStart,
    periodStop,
    flowStats,
    flowReady,
  } = props

  const getNewData = useCallback((address, start, stop, valueKey) => {
    let periodStartDate = start || periodStart
    periodStartDate = periodStartDate.substr(0, 10)
    let periodStopDate = stop || periodStop
    periodStopDate = periodStopDate.substr(0, 10)
    AppDispatcher.dispatch({
      action: 'get-stats-flow-address',
      address: address || device.address,
      timeBeg: periodStartDate,
      timeEnd: periodStopDate,
      valueKey: valueKey || searchParams.get('valueKey'),
    })
  }, [device.address, periodStart, periodStop, searchParams])

  useEffect(() => {
    getNewData()
  }, [getNewData])

  useEffect(() => {
    const interval = setInterval(getNewData, 60000)
    return () => clearInterval(interval)
  }, [getNewData])

  useEffect(() => {
    getNewData(null, periodStart, periodStop, searchParams.get('valueKey'))
  }, [periodStart, periodStop, searchParams, getNewData])

  if (
    flowReady !== 'loaded'
  ) {
    return (
      <ChartDataLoading />
    )
  }

  const {
    locale: {
      unitsOfVolume = 'acreFeet',
      unitsOfFlow = 'gpm',
    } = {},
  } = AppStore.getUser() || {}

  let volumeTitle = 'Acre Feet'
  switch (unitsOfVolume) {
    case 'liters':
      volumeTitle = 'Liters'
      break
    case 'gallons':
      volumeTitle = 'Gallons'
      break
    case 'acreInch':
      volumeTitle = 'Acre Inch'
      break
    case 'acreFeet':
      volumeTitle = 'Acre Feet'
      break
    case 'cubicFeet':
      volumeTitle = 'Cubic Feet'
      break
    case 'hectareM':
      volumeTitle = 'Hectare Meter'
      break
    case 'cubicM':
      volumeTitle = 'Cubic Meter'
      break
    default:
      volumeTitle = 'Acre Feet'
  }

  let formula = ''
  if (searchParams.get('formula')) {
    formula = searchParams.get('formula')
  }

  let statType = ''
  if (searchParams.get('statType')) {
    statType = searchParams.get('statType')
  }

  let precision = 0
  if (searchParams.get('precision')) {
    const precisionParam = searchParams.get('precision')
    if (!isNaN(precisionParam) || precisionParam.indexOf(',') > -1) {
      precision = Number(precisionParam)
    }
  }

  let label = 'Stat'
  if (searchParams.get('label')) {
    label = searchParams.get('label')
  }

  let labelTwo = ''
  if (searchParams.get('labelTwo')) {
    labelTwo = searchParams.get('labelTwo')
  }

  let multiplier = 1
  if (searchParams.get('multiplier')) {
    const multiplierParam = searchParams.get('multiplier')
    if (!isNaN(multiplierParam) || multiplierParam.indexOf(',') > -1) {
      multiplier = Number(multiplierParam)
    }
  }

  return (
    <div className='deviceInfo'>
      <ChartFlow
        flowStats={flowStats}
        flowReady={flowReady}
        statType={statType}
        unitsOfVolume={unitsOfVolume}
        unitsOfFlow={unitsOfFlow}
        volumeTitle={volumeTitle}
      />
      <br />
      <br />
      <Table
        flowStats={flowStats.daily_summary}
        series={flowStats.series}
        physical={device.physical}
        statType={statType}
        formula={formula}
        label={label}
        labelTwo={labelTwo}
        multiplier={multiplier}
        precision={precision}
        unitsOfVolume={unitsOfVolume}
        unitsOfFlow={unitsOfFlow}
        volumeTitle={volumeTitle}
      />
    </div>
  )
}

TelemetryFlow.propTypes = {
  device: PropTypes.shape({
    address: PropTypes.string,
    physical: PropTypes.object,
  }),
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
  flowStats: PropTypes.object,
  flowReady: PropTypes.string,
}

export default TelemetryFlow
